import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'
import { useAuthStore } from '@/stores/auth-store'

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const login = useAuthStore((s) => s.login)

  const [tenantCode, setTenantCode] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const phoneValid = /^1[3-9]\d{9}$/.test(phone)
  const canSubmit = tenantCode.trim().length > 0 && phoneValid && password.length > 0 && !loading

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return

    setError('')
    setLoading(true)
    try {
      await login(phone, password, tenantCode.trim())
      const returnUrl = searchParams.get('returnUrl') || '/'
      navigate(returnUrl, { replace: true })
    } catch (err: any) {
      const msg = err?.message || err?.toString() || ''
      if (msg.includes('not found') || msg.includes('password')) {
        setError('手机号或密码错误')
      } else if (msg.includes('network') || msg.includes('timeout')) {
        setError('网络错误，请稍后重试')
      } else {
        setError(msg || '登录失败，请重试')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary px-4">
      <Card className="w-full max-w-[440px] border-0 shadow-[var(--shadow-16)]">
        <CardHeader className="px-11 pt-10 pb-8">
          {/* Microsoft-style 4-color logo */}
          <div className="grid grid-cols-2 w-[21px] h-[21px] gap-[2px] mb-5">
            <div className="bg-[#f25022]" />
            <div className="bg-[#7fba00]" />
            <div className="bg-[#00a4ef]" />
            <div className="bg-[#ffb900]" />
          </div>
          <CardTitle className="text-2xl font-semibold">必竟宜居</CardTitle>
          <CardDescription className="text-sm mt-1">登录到企业数据管理平台</CardDescription>
        </CardHeader>
        <CardContent className="px-11 pb-10">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {error && (
              <Alert variant="destructive" className="text-sm">
                {error}
              </Alert>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="tenantCode">商家租户编码</Label>
              <Input
                id="tenantCode"
                type="text"
                placeholder="请输入商家租户编码"
                autoFocus
                autoComplete="organization"
                value={tenantCode}
                onChange={(e) => setTenantCode(e.target.value.trim())}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="phone">手机号</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="请输入手机号"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">密码</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="请输入密码"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full mt-4" disabled={!canSubmit}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  登录中...
                </>
              ) : (
                '登录'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
